// @ts-nocheck
// tool-shopify-plans.js — the ONE place Shopify plan prices and rates live in this theme.
//
// Ported from the Vercel app's src/lib/shopifyPricing.ts. Source of truth for every
// figure below: https://www.shopify.com/pricing, verified October 2026. Re-verify
// whenever Shopify changes plan pricing or card rates, and bump RATES_AS_OF when you do,
// because the UI shows that date.
//
// ─────────────────────────────────────────────────────────────────────────────────
// COST IS NOT THE RECOMMENDATION (SSM-574)
//
// Ranking on processing cost alone told a merchant doing $500,000/mo that Advanced was
// their answer. That arithmetic is correct and the advice is wrong, because the cost
// function could not see the things that actually decide the plan at that size:
// storefront count, staff seats, checkout customization, B2B. Four storefronts at $500k
// puts Advanced at $14,696 against Plus at $14,550 on raw cost alone.
//
// So the model has two independent halves and they must stay independent:
//   - subscriptionCost/comparePlans answer "what does each plan cost".
//   - eligiblePlans answers "which plans can this merchant actually use".
// recommendPlan combines them WITHOUT ever letting a gate become a dollar amount or a
// dollar amount silence a gate. Do not collapse them into one score.
// ─────────────────────────────────────────────────────────────────────────────────
//
// Consumed by:
//   assets/tool-shopify-pricing.js       (/pages/seo-tools/shopify-pricing-calculator)
//   assets/tool-shopify-plan-picker.js   (/pages/seo-tools/shopify-plan-picker)
//   assets/shopify-pricing-calculator.js (blog: insights/shopify-pricing)
//
// Registered before its consumers; `defer` preserves document order. Exposed on
// window because the theme's widget scripts are plain IIFEs, not modules.
//
// ─────────────────────────────────────────────────────────────────────────────────
// THE INVARIANT THIS FILE EXISTS TO PROTECT
//
// In TypeScript, the `PlanKey` union physically prevented an off-ladder plan from
// reaching a ranking. There is no type system here, so it is enforced structurally:
// SIMPLE_PLANS is a SEPARATE ARRAY, and every ranking/compare/break-even function below
// iterates PLAN_KEYS and nothing else. Do not add an off-ladder plan to PLANS or
// PLAN_KEYS. Agentic is $0/mo at 2.9%, the same card rate as Basic at $39/mo, so in a
// single ranking it strictly dominates Basic — which would go from winning every store
// under ~$33,000/mo to never being recommended at any revenue.
// ─────────────────────────────────────────────────────────────────────────────────
(function () {
  var PER_ORDER_FEE = 0.3;
  var RATES_AS_OF = 'October 2026';

  // Each storefront beyond the 10 a Plus contract includes. Non-Plus plans have no
  // equivalent: every extra storefront there is a full second subscription.
  var EXPANSION_STORE_FEE = 300;

  // Above this monthly revenue the tool stops presenting its totals as a decision.
  // App spend, checkout conversion and negotiated Plus terms all outweigh the gaps it
  // can see, and none of them are modelled.
  var INDICATIVE_REVENUE = 250000;

  // Annual sales at which Shero recommends Shopify Plus. This is advice, not fee math:
  // on fees alone Plus only overtakes Advanced near $760,000/mo, which would tell a
  // $1M to $9M a year merchant to stay on Advanced. That is the opposite of what Shero
  // tells merchants, because checkout customization, full B2B,
  // expansion storefronts, unlimited staff and priority support decide the plan long
  // before the card-rate gap does. Callers show the cost difference next to this
  // recommendation so it is never hidden.
  var PLUS_RECOMMENDED_ANNUAL = 1000000;

  function isPlusRecommended(monthlyRevenue) {
    return monthlyRevenue * 12 >= PLUS_RECOMMENDED_ANNUAL;
  }

  // Shopify's own surcharge for using a third-party gateway, as a decimal. Shopify's
  // cut ONLY: it does not include what the third-party processor itself charges, so any
  // total built on it understates a third-party store's real bill. Say so wherever it
  // surfaces. Plus figure confirmed at shopify.com/plus/pricing: "plus 0.20% per
  // transaction to Shopify for security and compliance costs".
  var THIRD_PARTY_SURCHARGE = {
    basic: 0.02,
    grow: 0.01,
    advanced: 0.006,
    plus: 0.002
  };

  // annualSub null means Shopify publishes no annual price for that plan, in which case
  // callers fall back to sub. rateIsEstimate true means the rate is our assumption
  // rather than a published figure, and the UI has to say so.
  //
  // The Plus rate was 0.0215 and flagged as an estimate. shopify.com/pricing now
  // publishes 2.25% + 30¢, so it is a cited figure. Correcting it moves the
  // Advanced-to-Plus crossover from about $543,000 to about $760,000 on monthly billing,
  // which is why it had to land together with the eligibility gates below rather than on
  // its own. `subOneYear` is the 1-year commitment price; `sub` is the 3-year floor.
  var PLANS = {
    basic: { name: 'Basic', sub: 39, annualSub: 29, rate: 0.029, perOrder: PER_ORDER_FEE, rateIsEstimate: false },
    grow: { name: 'Grow', sub: 105, annualSub: 79, rate: 0.027, perOrder: PER_ORDER_FEE, rateIsEstimate: false },
    advanced: { name: 'Advanced', sub: 399, annualSub: 299, rate: 0.025, perOrder: PER_ORDER_FEE, rateIsEstimate: false },
    plus: { name: 'Shopify Plus', sub: 2300, subOneYear: 2500, annualSub: null, rate: 0.0225, perOrder: PER_ORDER_FEE, rateIsEstimate: false }
  };

  /*
   * Hard capability limits per plan, from shopify.com/pricing.
   *
   * These are ELIGIBILITY, never a cost term. A merchant who needs 20 staff seats cannot
   * buy Advanced at any price, so no dollar comparison involving Advanced means anything
   * to them. Keep them out of every total.
   *
   * `staff` is additional staff accounts beyond the store owner, which is how Shopify
   * counts them: Basic publishes 0, meaning the owner alone.
   * `stores` is storefronts included before the per-store fee starts.
   *
   * Only what the UI can actually ask about. Locations (10 below Plus, 200 on Plus) and
   * B2B catalogs (3 below Plus) also differ by plan, but they decide the plan for almost
   * nobody, so they are left out rather than modelled and never reached.
   */
  var PLAN_LIMITS = {
    basic: { staff: 0, stores: 1, checkout: false },
    grow: { staff: 5, stores: 1, checkout: false },
    advanced: { staff: 15, stores: 1, checkout: false },
    plus: { staff: Infinity, stores: 10, checkout: true }
  };

  // Cheapest subscription first. The selects, the comparison tables and the break-even
  // walk all read this, so they cannot fall out of sync.
  var PLAN_KEYS = ['basic', 'grow', 'advanced', 'plus'];

  var DEFAULT_PLAN = 'grow';
  var DEFAULT_PERIOD = 'monthly';
  var DEFAULT_GATEWAY = 'shopify';

  // Preset monthly revenue tiers. The UI adds a custom-entry option alongside these.
  var REVENUE_TIERS = [5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000];

  function planFor(plan) {
    return PLANS[plan];
  }

  // `published` is false when we fell back to the monthly price because no annual price
  // exists, so the UI can flag that cell instead of implying a discount not offered.
  function subFor(plan, period) {
    var config = planFor(plan);
    if (period === 'annual' && config.annualSub !== null) {
      return { amount: config.annualSub, published: true };
    }
    return { amount: config.sub, published: period === 'monthly' };
  }

  /*
   * What the merchant pays in subscription for `stores` storefronts on `plan`.
   *
   * The asymmetry here is the whole point. On Basic, Grow and Advanced a second
   * storefront is a second full subscription with its own admin and its own inventory.
   * A Plus contract includes 10 and charges EXPANSION_STORE_FEE beyond that. At four
   * storefronts on $500,000/mo that is Advanced $1,196 against Plus $2,300 in
   * subscription, but Plus claws it back on the rate and wins the total. A single-store
   * model cannot see that, which is what SSM-574 was about.
   *
   * `stores` below 1 is treated as 1: a merchant always has at least one storefront.
   */
  function subscriptionCost(plan, period, stores) {
    var base = subFor(plan, period).amount;
    var count = stores > 1 ? Math.floor(stores) : 1;
    if (plan === 'plus') {
      return base + Math.max(0, count - PLAN_LIMITS.plus.stores) * EXPANSION_STORE_FEE;
    }
    return base * count;
  }

  // Rounded: a fractional order cannot be charged a per-transaction fee.
  function calcOrders(revenue, aov) {
    return aov > 0 ? Math.round(revenue / aov) : 0;
  }

  // Takes a plan OBJECT, so SIMPLE_PLANS entries (which have no key in PLANS) can use it.
  // Gateway-aware callers want calcTxFeesFor below instead.
  function calcTxFees(revenue, orders, plan) {
    return revenue * plan.rate + orders * plan.perOrder;
  }

  // Takes a plan KEY, so the third-party surcharge can be looked up. Every ladder
  // comparison goes through this rather than calcTxFees.
  function calcTxFeesFor(revenue, orders, planKey, gateway) {
    return revenue * effectiveRate(planKey, gateway) + orders * planFor(planKey).perOrder;
  }

  // Card rate as a display string with trailing zeros trimmed: 2.9, 2.15, 3. No '%';
  // callers append it.
  function formatRate(rate) {
    return String(parseFloat((rate * 100).toFixed(2)));
  }

  // The share of revenue a plan takes once the gateway is accounted for. EVERY
  // percentage-of-revenue comparison between plans must go through here, because the
  // surcharge differs per plan and so changes which plan wins.
  function effectiveRate(plan, gateway) {
    var g = gateway || DEFAULT_GATEWAY;
    return planFor(plan).rate + (g === 'third-party' ? THIRD_PARTY_SURCHARGE[plan] : 0);
  }

  function planFootnote(plan, period) {
    var p = period || DEFAULT_PERIOD;
    var config = planFor(plan);
    var s = subFor(plan, p);
    var billing = p === 'annual' && s.published ? '/mo billed annually' : '/mo';
    return (
      config.name + ': $' + s.amount + billing + ' + ' + formatRate(config.rate) +
      '% + $' + config.perOrder.toFixed(2) + ' per order.'
    );
  }

  /*
   * Totals for ONE plan at the merchant's numbers.
   *
   * With no revenue or no AOV every figure comes back null, NOT the bare subscription.
   * It used to return `total: sub`, and the UI printed it: picking "$500,000" without
   * yet typing an AOV rendered "$105.00" as a $6M/yr merchant's monthly cost. A
   * subscription on its own is not a total, so there is nothing truthful to show and the
   * caller must render an em-dash.
   */
  function calcResult(input) {
    var period = input.period || DEFAULT_PERIOD;
    var gateway = input.gateway || DEFAULT_GATEWAY;
    var footnote = planFootnote(input.plan, period);
    var hasInputs = input.revenue > 0 && input.aov > 0;

    if (!hasInputs) {
      return { orders: null, txFees: null, total: null, footnote: footnote, hasInputs: false };
    }

    var sub = subscriptionCost(input.plan, period, input.stores);
    var orders = calcOrders(input.revenue, input.aov);
    var txFees = calcTxFeesFor(input.revenue, orders, input.plan, gateway);
    return { orders: orders, txFees: txFees, total: sub + txFees, footnote: footnote, hasInputs: true };
  }

  // Rows come back in PLAN_KEYS order rather than sorted by price, so the table never
  // reorders itself while you type. Order count is computed once: the per-order fee is
  // identical on every plan, so it does not vary by row.
  //
  // `eligible` rides along per row but is NOT used to pick cheapestKey. That stays a
  // pure cost answer; recommendPlan is what combines the two.
  function comparePlans(input) {
    var period = input.period || DEFAULT_PERIOD;
    var gateway = input.gateway || DEFAULT_GATEWAY;
    var hasInputs = input.revenue > 0 && input.aov > 0;
    var orders = calcOrders(input.revenue, input.aov);
    var gates = eligiblePlans(input);

    var rows = PLAN_KEYS.map(function (key) {
      var config = planFor(key);
      var s = subFor(key, period);
      var sub = subscriptionCost(key, period, input.stores);
      var txFees = hasInputs ? calcTxFeesFor(input.revenue, orders, key, gateway) : 0;
      return {
        key: key,
        name: config.name,
        sub: sub,
        subPublished: s.published,
        rate: config.rate,
        rateIsEstimate: config.rateIsEstimate,
        txFees: txFees,
        total: sub + txFees,
        eligible: gates.eligible.indexOf(key) !== -1,
        blockedBy: gates.blockedBy[key] || null
      };
    });

    // Tie rule: walk in PLAN_KEYS order and only take a STRICTLY lower total, so at
    // exactly a break-even revenue the cheaper-subscription plan wins. Deterministic,
    // which stops the "Lowest cost" marker flickering as you cross a threshold.
    var cheapestKey = rows[0].key;
    var cheapestTotal = rows[0].total;
    for (var i = 1; i < rows.length; i++) {
      if (rows[i].total < cheapestTotal) {
        cheapestKey = rows[i].key;
        cheapestTotal = rows[i].total;
      }
    }

    // Same walk, restricted to plans the merchant can actually buy.
    var cheapestEligibleKey = null;
    var cheapestEligibleTotal = Infinity;
    for (var j = 0; j < rows.length; j++) {
      if (rows[j].eligible && rows[j].total < cheapestEligibleTotal) {
        cheapestEligibleKey = rows[j].key;
        cheapestEligibleTotal = rows[j].total;
      }
    }

    return {
      orders: orders,
      hasInputs: hasInputs,
      cheapestKey: cheapestKey,
      cheapestEligibleKey: cheapestEligibleKey,
      rows: rows
    };
  }

  /*
   * Which plans the merchant can actually buy, and why each of the others is out.
   *
   * Requirements are optional: an absent field never blocks anything, so a merchant who
   * answers nothing gets all four plans and the old cost-only behaviour. That keeps the
   * gates additive rather than a new way for the tool to be confidently wrong.
   *
   * `blockedBy` holds one short human reason per excluded plan, for the UI to quote.
   * Plus is never blocked: it clears every published limit.
   */
  function eligiblePlans(req) {
    var staff = req && req.staff > 0 ? Math.floor(req.staff) : 0;
    var needsCheckout = !!(req && req.needsCheckout);
    // Moved over from the Plan Picker's Plus-fit questions. Lower plans can sell
    // wholesale and to other countries in a limited way, so these are Shero's
    // recommendation rather than a hard Shopify limit, worded that way in the reason.
    var needsB2B = !!(req && req.needsB2B);
    var needsIntl = !!(req && req.needsIntl);

    // Shopify counts staff accounts as seats BEYOND the store owner.
    var extraStaff = Math.max(0, staff - 1);

    var eligible = [];
    var blockedBy = {};

    PLAN_KEYS.forEach(function (key) {
      var limit = PLAN_LIMITS[key];
      var reason = null;

      if (extraStaff > limit.staff) {
        reason =
          limit.staff === 0
            ? planFor(key).name + ' includes no staff accounts beyond the owner'
            : planFor(key).name + ' includes ' + limit.staff + ' staff accounts';
      } else if (needsCheckout && !limit.checkout) {
        reason = 'checkout customization is Plus only';
      } else if (needsB2B && key !== 'plus') {
        reason = 'full B2B and wholesale are on Plus';
      } else if (needsIntl && key !== 'plus') {
        reason = 'Plus is the plan for selling in several countries';
      }

      if (reason) {
        blockedBy[key] = reason;
      } else {
        eligible.push(key);
      }
    });

    return { eligible: eligible, blockedBy: blockedBy };
  }

  /*
   * The answer the UI shows. Deliberately NOT a single winner.
   *
   * `cheapestEligible` is the cost answer among plans the merchant can buy.
   * `gatedTo` is set only when a hard limit removed a cheaper plan, so the UI can say
   * why in its own sentence rather than burying it in a dollar figure.
   * `indicative` marks the range where the totals are context, not a decision.
   */
  function recommendPlan(input) {
    var comparison = comparePlans(input);
    if (!comparison.hasInputs) {
      return {
        hasInputs: false,
        cheapestEligible: null,
        cheapestOverall: null,
        gatedTo: null,
        blockedBy: {},
        indicative: false
      };
    }

    var gates = eligiblePlans(input);
    var cheapestEligible = comparison.cheapestEligibleKey;
    var cheapestOverall = comparison.cheapestKey;

    return {
      hasInputs: true,
      cheapestEligible: cheapestEligible,
      cheapestOverall: cheapestOverall,
      // Only when eligibility actually changed the answer.
      gatedTo: cheapestEligible && cheapestEligible !== cheapestOverall ? cheapestEligible : null,
      blockedBy: gates.blockedBy,
      indicative: input.revenue >= INDICATIVE_REVENUE || cheapestEligible === 'plus'
    };
  }

  /*
   * Monthly revenue at which plan `b` becomes cheaper than plan `a`.
   *
   * total = sub + revenue * effectiveRate + orders * perOrder. The per-order fee is
   * $0.30 on every plan, so that term is identical on both sides and cancels, leaving
   * (subB - subA) / (effRateA - effRateB). This is why the threshold depends only on
   * revenue and is completely independent of AOV. Do NOT "fix" this by adding the
   * per-order term back in.
   *
   * The third-party surcharge is the opposite case and must NOT be cancelled: it is a
   * percentage of revenue that differs per plan, so it belongs inside the rate gap.
   * Dropping it moves the Basic-to-Grow threshold from $5,500 to $33,000.
   *
   * null when `b` has no rate advantage, so it never overtakes `a`.
   * 0 when `b` is cheaper on both subscription and rate, so it wins immediately.
   */
  function breakEvenRevenue(a, b, period, gateway) {
    var p = period || DEFAULT_PERIOD;
    var g = gateway || DEFAULT_GATEWAY;
    var rateGap = effectiveRate(a, g) - effectiveRate(b, g);
    if (rateGap <= 0) return null;
    var subGap = subFor(b, p).amount - subFor(a, p).amount;
    if (subGap <= 0) return 0;
    return subGap / rateGap;
  }

  function adjacentBreakEvens(period, gateway) {
    var p = period || DEFAULT_PERIOD;
    var g = gateway || DEFAULT_GATEWAY;
    var out = [];
    for (var i = 0; i < PLAN_KEYS.length - 1; i++) {
      var from = PLAN_KEYS[i];
      var to = PLAN_KEYS[i + 1];
      var revenue = breakEvenRevenue(from, to, p, g);
      if (revenue === null) continue;
      out.push({
        from: from,
        fromName: planFor(from).name,
        to: to,
        toName: planFor(to).name,
        revenue: revenue,
        approximate:
          planFor(from).rateIsEstimate ||
          planFor(to).rateIsEstimate ||
          !subFor(from, p).published ||
          !subFor(to, p).published
      });
    }
    return out;
  }

  function savingsBetween(from, to, input) {
    var period = input.period || DEFAULT_PERIOD;
    function at(key) {
      return calcResult({
        plan: key,
        revenue: input.revenue,
        aov: input.aov,
        period: period,
        stores: input.stores,
        gateway: input.gateway
      }).total;
    }
    var a = at(from);
    var b = at(to);
    // calcResult returns null totals without inputs; there is no saving to state then.
    if (a == null || b == null) return { perMonth: 0, perYear: 0 };
    var perMonth = Math.max(0, a - b);
    return { perMonth: perMonth, perYear: perMonth * 12 };
  }

  /*
   * True when `from` is Shopify Plus and `to` sits below it on the ladder.
   *
   * Cost math ranks Plus on price like any other plan, so below the Advanced-to-Plus
   * crossover it reports a saving for LEAVING Plus. That is a correct arithmetic result
   * and a bad thing to hand a merchant:
   *   1. The gap sits inside its own error bar. The Plus rate is an estimate, and at
   *      $500,000/mo a 0.05pp error moves the Plus bill by $250 — more than the gap.
   *   2. PLANS.plus.sub is Shopify's published three-year commitment price, not a quote.
   *      Plus is negotiated case by case.
   *   3. Nothing Plus is bought for (checkout extensibility, native B2B, expansion
   *      tooling, API limits, dedicated support) appears in a cost total.
   *
   * Callers suppress the move-plans prompt and show a neutral note instead. They must
   * NOT hide the totals: the table stays accurate, only the instruction goes.
   */
  function isPlusDowngrade(from, to) {
    return from === 'plus' && PLAN_KEYS.indexOf(to) < PLAN_KEYS.indexOf(from);
  }

  // Read from PLANS rather than written down, so a figure quoted in copy cannot drift
  // from the rate table. Never negative: `to` is expected to be the higher plan.
  function rateSavingBetween(from, to, gateway) {
    var g = gateway || DEFAULT_GATEWAY;
    return Math.max(0, effectiveRate(from, g) - effectiveRate(to, g));
  }

  // The widest processing-rate gap on offer: cheapest subscription to most expensive.
  // Shopify's cut only; never the merchant's own processor rate.
  function maxRateSaving(gateway) {
    return rateSavingBetween(PLAN_KEYS[0], PLAN_KEYS[PLAN_KEYS.length - 1], gateway);
  }

  // ── Plans Shopify sells OUTSIDE the four-plan ladder ──────────────────────
  // Priced as context, NEVER ranked. See the invariant at the top of this file.
  var SIMPLE_PLANS_AS_OF = 'September 2026';

  var SIMPLE_PLANS = [
    {
      key: 'agentic',
      name: 'Agentic',
      sub: 0,
      rate: 0.029,
      perOrder: PER_ORDER_FEE,
      // Verbatim from shopify.com/pricing.
      positioning: 'For selling in AI channels',
      limitation:
        'Lists your products in Shopify’s catalogue for AI channels to sell from. There is no online store of your own, so it sits alongside a storefront plan rather than replacing one.',
      href: 'https://www.shopify.com/agentic-plan'
    }
    // Starter is intentionally absent. Shopify renders its price and card rate
    // client-side, so neither could be verified at the primary source, and this repo
    // does not ship a figure it cannot cite.
  ];

  function simplePlanCost(plan, revenue, aov) {
    var orders = calcOrders(revenue, aov);
    var txFees = calcTxFees(revenue, orders, plan);
    return { orders: orders, txFees: txFees, total: plan.sub + txFees };
  }

  window.SheroPlans = {
    PER_ORDER_FEE: PER_ORDER_FEE,
    RATES_AS_OF: RATES_AS_OF,
    EXPANSION_STORE_FEE: EXPANSION_STORE_FEE,
    INDICATIVE_REVENUE: INDICATIVE_REVENUE,
    PLUS_RECOMMENDED_ANNUAL: PLUS_RECOMMENDED_ANNUAL,
    isPlusRecommended: isPlusRecommended,
    THIRD_PARTY_SURCHARGE: THIRD_PARTY_SURCHARGE,
    PLANS: PLANS,
    PLAN_LIMITS: PLAN_LIMITS,
    PLAN_KEYS: PLAN_KEYS,
    DEFAULT_PLAN: DEFAULT_PLAN,
    DEFAULT_PERIOD: DEFAULT_PERIOD,
    DEFAULT_GATEWAY: DEFAULT_GATEWAY,
    REVENUE_TIERS: REVENUE_TIERS,
    planFor: planFor,
    subFor: subFor,
    subscriptionCost: subscriptionCost,
    calcOrders: calcOrders,
    calcTxFees: calcTxFees,
    calcTxFeesFor: calcTxFeesFor,
    formatRate: formatRate,
    effectiveRate: effectiveRate,
    planFootnote: planFootnote,
    calcResult: calcResult,
    comparePlans: comparePlans,
    eligiblePlans: eligiblePlans,
    recommendPlan: recommendPlan,
    breakEvenRevenue: breakEvenRevenue,
    adjacentBreakEvens: adjacentBreakEvens,
    savingsBetween: savingsBetween,
    isPlusDowngrade: isPlusDowngrade,
    rateSavingBetween: rateSavingBetween,
    maxRateSaving: maxRateSaving,
    SIMPLE_PLANS_AS_OF: SIMPLE_PLANS_AS_OF,
    SIMPLE_PLANS: SIMPLE_PLANS,
    simplePlanCost: simplePlanCost
  };
})();
